import axios from "axios";
import { setupCache, buildMemoryStorage } from "axios-cache-interceptor";
import { useCallback, useState } from "react";
import { useAuth } from "./useAuth";
import { useErrorTracking } from "../utils/errorTracking";
import { checkSessionAfter401 } from "../utils/sessionCheck";

const API_URL_PREFIX = process.env.API_URL;

// Create cached axios instance with ETag support
const cachedAxios = setupCache(axios.create(), {
  // Interpret Cache-Control headers from server
  interpretHeader: true,

  // Enable ETag support for 304 Not Modified responses
  etag: true,

  // Cache GET requests only (not POST, PUT, DELETE)
  methods: ["get"],

  // Time to live for cached responses (ms) - can be overridden by Cache-Control
  ttl: 1000 * 30, // 30 seconds default

  // Storage type — in-memory only (GO-3121). The 30s TTL above makes
  // cross-session persistence pointless, and persisting GET responses to
  // localStorage (axios-cache-interceptor never proactively evicts) grew the
  // "office-web-cache:" namespace until it filled the ~5MB quota and unrelated
  // localStorage writes threw QuotaExceededError (crashing the Library page).
  // Memory storage = zero localStorage pressure, same in-session cache + ETag.
  storage: buildMemoryStorage(),

  // Handle 304 responses automatically
  cachePredicate: {
    statusCheck: (status) => status >= 200 && status < 400,
  },
});

const useApi = (logoutOnError = true) => {
  const { userInfo, logout } = useAuth();
  const { token } = userInfo;
  const { trackError } = useErrorTracking();

  const [response, setResponse] = useState(false);
  const [status, setStatus] = useState("uninitialized");

  const clear = () => {
    setResponse(false);
    setStatus("uninitialized");
  };

  const sendRequest = ({
    endpoint,
    method,
    data,
    addPrefix = true,
    addContentType,
    abortController,
    addContentTypeMedia,
    addXApplicationHeader = true,
    addToken = true,
    // When true, use minimal headers and bypass cached axios to avoid CORS issues
    isExternalApi = false,
  }) => {
    setStatus("wait");

    // Build headers conditionally. For external APIs, do not send any
    // custom headers (including Cache-Control or Authorization) to avoid
    // triggering CORS preflight rejections.
    const headers = isExternalApi
      ? {}
      : {
          "Content-Type": addContentType
            ? "application/json"
            : addContentTypeMedia
            ? "multipart/form-data"
            : "",
          "x-application": addXApplicationHeader ? `office.web` : undefined,
          ...(addToken ? { Authorization: `Bearer ${token}` } : {}),
        };

    // A 401 only says something about our session if the request actually sent
    // our credentials. Tokenless and external calls (ushop, solar, unicity.link)
    // answer 401 about themselves, so logging an authenticated distributor out
    // on one would be the spurious logout that got this disabled in PR #204.
    const sentCredentials = addToken && !isExternalApi;

    return new Promise((resolve, reject) => {
      // Use cached axios for GET requests except when calling external APIs
      // where we must avoid added headers that cause CORS issues.
      const axiosInstance =
        !isExternalApi && method?.toLowerCase() === "get" ? cachedAxios : axios;

      axiosInstance({
        method: method,
        url: addPrefix ? `${API_URL_PREFIX}/${endpoint}` : endpoint,
        headers: headers,
        data: data,
        ...(abortController && { signal: abortController.signal }),
        // Accept 304 Not Modified as successful response
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 304,
      })
        .then((response) => {
          setStatus("complete");
          setResponse(response);

          // Log cache status for debugging (optional - remove in production)
          if (response.cached) {
            console.log(`[Cache HIT] ${endpoint}`);
          }

          resolve(response);
        })
        .catch(async (err) => {
          if (!axios.isCancel(err)) {
            // A credentialed 401 can still be a live session that Hydra won't
            // let read this one resource (GO-3538), so confirm it before
            // logging out. The caller sees the error once that's settled.
            if (
              logoutOnError &&
              sentCredentials &&
              [401].indexOf(err?.response?.status) !== -1
            ) {
              await checkSessionAfter401({
                token,
                logout,
                error: err,
                endpoint,
                method,
              });
            }

            setStatus("error");
            setResponse(err);

            // Track error after existing error handling
            trackError(err, {
              userId: userInfo?.id,
              userCountry: userInfo?.country,
              business: {
                feature: endpoint?.split("/")[0] || "unknown",
                process: method === "get" ? "fetch" : "update",
                impact: err.response?.status >= 500 ? "high" : "medium",
                customerType: userInfo?.type,
              },
            });

            reject(err);
          }
        });
    });
  };

  // Accept 304 Not Modified as successful response
  const validateStatus = (status) =>
    (status >= 200 && status < 300) || status === 304;

  const sendRequestAsCallback = useCallback(
    async ({
      endpoint,
      method,
      data,
      addPrefix = true,
      addContentType,
      abortController,
      addContentTypeMedia,
      acceptLanguage,
      addToken = true,
    }) => {
      setStatus("wait");

      const headers = {
        Authorization: addToken ? `Bearer ${token}` : "",
        "Content-Type": addContentType
          ? "application/json"
          : addContentTypeMedia
          ? "multipart/form-data"
          : "",
        "Accept-Language": acceptLanguage ? acceptLanguage : "",
        "x-application": `office.web`,
      };

      return new Promise((resolve, reject) => {
        // Use cached axios for GET requests, regular axios for others
        const axiosInstance =
          method?.toLowerCase() === "get" ? cachedAxios : axios;

        axiosInstance({
          method: method,
          url: addPrefix ? `${API_URL_PREFIX}/${endpoint}` : endpoint,
          headers: headers,
          data: data,
          ...(abortController && { signal: abortController.signal }),
          validateStatus,
        })
          .then((response) => {
            setStatus("complete");
            setResponse(response);

            // Log cache status for debugging (optional - remove in production)
            if (response.cached) {
              console.log(`[Cache HIT] ${endpoint}`);
            }

            resolve(response);
          })
          .catch(async (err) => {
            if (!axios.isCancel(err)) {
              // Only a credentialed request's 401 can be about our session,
              // and even then it's confirmed first; see sendRequest above.
              if (
                logoutOnError &&
                addToken &&
                [401].indexOf(err?.response?.status) !== -1
              ) {
                await checkSessionAfter401({
                  token,
                  logout,
                  error: err,
                  endpoint,
                  method,
                });
              }

              setStatus("error");
              setResponse(err);

              // Track error after existing error handling
              trackError(err, {
                userId: userInfo?.id,
                userCountry: userInfo?.country,
                business: {
                  feature: endpoint?.split("/")[0] || "unknown",
                  process: method === "get" ? "fetch" : "update",
                  impact: err.response?.status >= 500 ? "high" : "medium",
                  customerType: userInfo?.type,
                },
              });

              reject(err);
            }
          });
      });
       
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logout, token]
  );

  return {
    response,
    status,
    validateStatus,
    sendRequest,
    clear,
    sendRequestAsCallback,
  };
};

export { useApi };
